import { useState } from 'react';
import { css } from '@emotion/core';
import t from 'react-translate';

import { Survey } from 'redux/schemas/models/survey';
import { AiSuggestedComponentProps } from 'redux/schemas/models/ai-course-creation';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';

import { gray5 } from 'styles/global_defaults/colors';
import { largeSpacing } from 'styles/global_defaults/scaffolding';

import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import LikertScale from 'shared/components/likert-scale';

interface SurveyQuestionProps extends AiSuggestedComponentProps {
  showFullQuestions?: boolean,
}

const SurveyQuestion = (props: SurveyQuestionProps) => {
  const { componentSet, showFullQuestions = false } = props;
  // By default, show only the first question. If showFullQuestions is true,
  // show all questions expanded and the expand button is not shown.
  const [expanded, setExpanded] = useState(showFullQuestions);
  const activity = componentSet.activity as LectureComponent<ComponentType.SURVEY>;
  const survey = activity.survey as Survey;

  const styles = css`
    .question-no {
      min-width: ${largeSpacing}px;
      height: ${largeSpacing}px;
      border-radius: 4px;
      border: 1px solid ${gray5};
    }
    .text-lg-bold {
      p {
        font-weight: 700;
      }
    }
  `;

  const getQuestionContent = (question) => {
    switch (question.type) {
      case QuizQuestionType.LIKERT_SCALE_QUESTION:
        return (
          <LikertScale
            isDisabled
            limits={{ min: { value: question.leftLimit }, max: { value: question.rightLimit } }}
          />
        );
      // Add more cases for different question types as needed
      default:
        return '';
    }
  };

  const showQuestionText = (questionText) => (
    <div
      className='text-lg-bold mb-2'
      dangerouslySetInnerHTML={{ __html: questionText }}
    />
  );

  return (
    <div className='d-flex flex-column mt-4' css={styles}>
      {/**
       * The first question text should always be shown and treated as read-only,
       * so only the text needs to be displayed.
       */}
      <div className='text-lg-bold'>{showQuestionText(survey.questions[0].questionText)}</div>
      {/* Show remaining questions only when expanded */}
      {expanded && survey.questions.slice(1).map((question, i) => (
        <div className='d-flex mt-4' key={question.questionIndex}>
          <div className='question-no d-flex align-items-center justify-content-center text-large-body bg-gray-7 mr-2'>
            {i + 1}
          </div>
          <div>
            {showQuestionText(question.questionText)}
            {getQuestionContent(question)}
          </div>
        </div>
      ))}

      {!showFullQuestions && survey.questions.length > 1 && (
        <ClickableContainer
          onClick={() => setExpanded((prev) => !prev)}
          className='d-flex align-items-center text-primary mt-4'
          aria-label={t.SHARED[expanded ? 'VIEW_LESS' : 'VIEW_MORE']()}
        >
          <span className='mr-1'>
            {t.SHARED[expanded ? 'VIEW_LESS' : 'VIEW_MORE']()}
          </span>
          <NvIcon
            icon={expanded ? 'arrow-up' : 'arrow-down'}
            size='xs-smallest'
          />
        </ClickableContainer>
      )}
    </div>
  );
};

export default SurveyQuestion;
